import { type Variants, AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-client';
import tailwindConfig from 'tailwind.config';

import { cn } from '@/lib/utils';

import { useMediaQuery } from '../hooks/useMediaQuery';
import { HeaderItem, HeaderItemProps } from './HeaderItem';
import { MenuAccordionContent } from './MenuAccordionContent';
import { MenuGroupProps } from './MenuGroup';
import { MenuSheet } from './MenuSheet';

export type MainLinkItemProps = {
  link: {
    item: Pick<
      HeaderItemProps,
      'title' | 'badge' | 'icon' | 'showDropdown' | 'href'
    >;
    menu: MenuGroupProps[];
  };
  isActive: boolean;
  isHovering: boolean;
  onLinkHover: (title?: string) => void;
  onLinkHoverCancel: (title?: string) => void;
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
};

export function MainLinkItem({
  link,
  isActive,
  isHovering,
  onLinkClick,
  onLinkHover,
  onLinkHoverCancel,
}: MainLinkItemProps) {
  const isWidthBelowXL = useMediaQuery(
    `(max-width: ${parseInt(tailwindConfig.theme.screens.headerLg) - 1}px)`
  );

  const handleMenuSheetClose = () => {
    onLinkClick?.({
      title: '',
    });

    onLinkHoverCancel();
  };

  return (
    <motion.li
      variants={itemVariants}
      className={cn(
        'group/header-list-item headerLg:p-0 headerLg:focus-within:bg-transparent headerLg:hover-hover:hover:bg-transparent self-stretch transition-colors duration-300',
        !isActive &&
          'hover-hover:hover:bg-gray-dark-100 headerLg:focus-within:[&:has(:focus-visible)]:bg-transparent focus-within:[&:has(:focus-visible)]:bg-gray-dark-100'
      )}
      data-hovered={isHovering && !isActive}
      onMouseEnter={() => onLinkHover(link.item.title)}
      onMouseLeave={() => onLinkHoverCancel()}
    >
      <div className='headerLg:h-full headerLg:m-0 headerLg:border-none border-gray-dark-200 mx-5 border-t group-first/header-list-item:border-none'>
        <HeaderItem
          icon={link.item.icon}
          badge={link.item.badge}
          title={link.item.title!}
          href={link.item.href}
          showDropdown={link.item.showDropdown}
          onItemClick={onLinkClick}
          expanded={isActive}
        />
      </div>
      <AnimatePresence>
        {isWidthBelowXL && (
          <motion.div
            className={cn(
              'grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out',
              isActive && 'grid-rows-[1fr]'
            )}
          >
            <MenuAccordionContent
              groups={link.menu}
              onLinkClick={onLinkClick}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!isWidthBelowXL && (
          <MenuSheet
            isOpen={isActive}
            menu={link.menu}
            onClose={handleMenuSheetClose}
            onLinkClick={onLinkClick}
          />
        )}
      </AnimatePresence>
    </motion.li>
  );
}

const itemVariants = {
  open: {
    x: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  closed: {
    x: 100,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
} satisfies Variants;
